import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Alert, alpha, Box, Grid, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useGlobalApproverSettings } from 'api/aws/approvers/fetchGlobalApprovers'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { computeTotalsFn } from 'api/aws/deals/computeTotalValuesFn'
import { useDealData } from 'api/aws/deals/useDealData'
import { useRequestApproval } from 'api/aws/deals/useRequestApproval'
import { useApprovalRequestFlow } from 'api/aws/deals/useRequestApprovalFlow'
import { useBatchUpdateItems } from 'api/aws/organizations_table/useBatchUpdateItems'
import { useBootstrapRefreshState } from 'api/aws/organizations_table/useBootstrapRefreshState'
import { useOrganizationView } from 'api/aws/organizations_table/useOrganizationView'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateFields } from 'api/aws/organizations_table/useUpdateFields'
import { useCreateTimeline } from 'api/aws/useCreateTimeline'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useGetDocument } from 'api/PandaDoc/useGetDocument'
import { useUpdateDocument } from 'api/PandaDoc/useUpdateDocument'
import { useEntitiesFromSearch } from 'api/search'
import { toServiceKey } from 'components/DataGrid/operators/formatKeys'
import ContractToolbar from 'components/Deals/contracts/ContractToolbar'
import ProductRowsTable from 'components/ProductRows/ProductRowsTable'
import ProductRowsTableView from 'components/ProductRows/ProductRowsTableView'
import { sortProducts } from 'components/ProductRows/sortProducts'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useApproverSettings } from 'context/Settings/useApproverSettings'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useDealEditingMachine } from 'hooks/useDealEditingMachine'
import { useOptimisticDealSave } from 'hooks/useOptimisticDealSave'
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { BoxScroller } from 'styles/elements/styleElements'
import { AgreementContainer, DealInfoPanel, ProductsSection } from 'styles/utilities/view-agreement'
import { GROBxLoader } from 'theme/components'
import { CurrentUser, NewDeal, Product, ProductSortMode } from 'types'
import { updateProductField as calcUpdateProductField, normalizeProduct } from 'utils/dealCalculations'
import { toMoney } from 'utils/toMoney'
import '../deals.css'
import { SaveProgressIndicator } from '../SaveProgressIndicator'
import { useCreateDocuments } from '../useCreateDocuments'
import { toAuthorized, toClient } from '../utils/recipientMappers'
import AgreementTotalsEdit from './AgreementTotalsEdit'
import AgreementTotalsView from './AgreementTotalsView'
import ContractFlowTimeline from './ContractFlowTimeline'
import ContractTotalsBar from './ContractTotalsBar'

type SaveAllResult = {
  savedDeal: NewDeal
  rowsSaved: Product[]
  totalsSaved: NewDeal['totals']
}
const MONEY_FIELDS = new Set([
  'price',
  'margin',
  'billedPrice',
  'mediaSpend',
  'total',
  'netTotal',
  // add others that are truly numeric
])
type ViewAgreementProps = {
  dealPK?: string
  dealSK?: string
}

const ViewAgreement = ({ dealPK, dealSK }: ViewAgreementProps = {}) => {
  const params = useParams()
  const PK = dealPK || params.PK
  const SK = dealSK || params.SK
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { syncDealFields } = useSyncQueryCache()
  const dealId = SK?.split('DEAL_')?.[1]
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [showRefreshBanner, setShowRefreshBanner] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sortMode, setSortMode] = useState<ProductSortMode>('NAME_CATEGORY_ASC')
  const [showPointsView, setShowPointsView] = useState(false)
  const lastStampRef = useRef<string | null>(null)
  const scrollRef = useRef(null)
  const { isItemsLoading, isItemsFetching, isReady: isSearchReady } = useEntitiesFromSearch()
  const { data: refreshState } = useBootstrapRefreshState()
  const finishedAt = refreshState?.finishedAt ?? 0
  const {
    deal,
    productRows,
    organization,
    restoreProductRows,
    validate,
    computeTotals,
    reassignRecipients,
  } = useDealData(PK, SK, dealId)
  useEffect(() => {
    console.log('deal', deal)
    console.log('productRows', productRows)
  }, [])
  // ─── Editing Machine ────────────────────────────────────────────────────
  const machine = useDealEditingMachine()

  // Sync React Query data into machine (baselines update when idle, source data updates when editing)
  // Guard: only dispatch LOAD_DATA when the deal or products actually change identity,
  // not on every React Query cache reference swap (which would cause an infinite loop).
  const loadDataFingerprint = useMemo(() => {
    const dealKey = deal?.SK ?? ''
    const dealMod = deal?.lastModified ?? deal?.creation_timestamp ?? ''
    const productKeys = productRows?.map(p => p.SK).join(',') ?? ''
    return `${dealKey}|${dealMod}|${productKeys}`
  }, [deal, productRows])
  const prevLoadFingerprint = useRef('')
  useEffect(() => {
    if (deal && productRows && loadDataFingerprint !== prevLoadFingerprint.current) {
      prevLoadFingerprint.current = loadDataFingerprint
      machine.loadData(deal, productRows)
    }
  }, [loadDataFingerprint])
  // Sync points toggle with deal billing model on initial load
  const pointsInitRef = useRef(false)
  useEffect(() => {
    if (deal?.billingModel === 'points' && !pointsInitRef.current) {
      pointsInitRef.current = true
      setShowPointsView(true)
    }
  }, [deal?.billingModel])

  // Derived display values: draft when editing, machine source (post-save) or React Query when idle
  const activeDeal = machine.isEditing ? machine.deal : deal
  // After save, machine.products has the confirmed server data — prefer it over
  // productRows (React Query cache) which may lag behind due to WebSocket race conditions.
  const editingRows = machine.isEditing
    ? machine.products
    : machine.products.length > 0
      ? machine.products
      : productRows ?? []
  const isSaveRequired = machine.isDirty
  // ─── End Editing Machine ─────────────────────────────────────────────────
  const parentOrgId = useMemo(() => organization?.contractedBy?.[0], [organization])
  const { organization: parentOrg, existingCustomProducts } = useOrganizationView(
    parentOrgId ? `ORG_${parentOrgId}` : null
  )
  const { requestApproval } = useRequestApproval()
  const { handleApprovalRequest, isRequesting } = useApprovalRequestFlow()
  const { saveOptimistic, progress: saveProgress, isSaving: isOptimisticSaving } = useOptimisticDealSave()
  const { createInternalContract } = useCreateDocuments()
  const [anchorEl, setAnchorEl] = useState(null)
  const { mutate: updateDocument } = useUpdateDocument()
  const { mutate: updateFields } = useUpdateFields()
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const { syncDealsCache } = useSyncQueryCache()

  const onEditProducts = () => {
    machine.startEdit()
    // Apply current sort mode to the draft products
    machine.sortProductsDraft(sortProducts([...(productRows ?? [])], sortMode))
  }

  const { pinned, agentDock, isAgentDockOpen } = useLayoutContext()
  const isAgentDocked = !!agentDock && isAgentDockOpen
  const { showSnackbar } = useSnackbarContext()
  const { mutateAsync: batchUpdate } = useBatchUpdateItems()
  ///////////////////////////////////// Fetch Data ///////////////////////////////////////
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { data: contacts, isLoading: isContactsLoading } = useContactsItems()
  const { document, isDocumentLoading } = useGetDocument(deal?.contractId, ['document', deal?.contractId])
  //////////////////////////// Handle Change for Draft ///////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////

  ///////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////// Assign Data //////////////////////////////////////
  const currentUser = localStorage.getItem('currentUser') ?? null
  const activeUser = JSON.parse(currentUser) ?? (null as CurrentUser)
  const ZERO: ReturnType<typeof computeTotals> = {
    grossProfit: 0,
    monthlyTotal: 0,
    mrr: 0,
    serviceMrr: 0,
    setupFee: 0,
    implementationFee: 0,
    spend: 0,
    total: 0,
    totalGrossProfit: 0,
  }

  const deferredRows = useDeferredValue(editingRows) // cheap frame after commit
  const editTotals = useMemo(() => (activeDeal ? computeTotals(activeDeal, deferredRows) : ZERO), [
    activeDeal,
    deferredRows,
    computeTotals,
  ])
  // Always compute live totals from products — never rely on stale deal.totals
  const viewTotals = useMemo(() => (deal && productRows?.length ? computeTotals(deal, productRows) : ZERO), [
    deal,
    productRows,
    computeTotals,
  ])
  const totals = machine.isEditing ? editTotals : viewTotals
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const displayTotals = useMemo(() => {
    const base = machine.isEditing ? editTotals : viewTotals

    // normalize CSA check + n
    const type = String(activeDeal?.type ?? '').toUpperCase()
    const isCSA = type === 'CSA' || type === 'RCSA'
    const toNum = (v: unknown) => (typeof v === 'string' ? parseFloat(v) || 0 : Number(v || 0))

    const n = isCSA ? Math.max(1, toNum(activeDeal?.numberOfPayments)) : 1
    // always (re)derive paymentAmount from the displayed total
    const paymentAmount =
      isCSA && n > 0 ? Math.round(((base.monthlyTotal || 0) / n + Number.EPSILON) * 100) / 100 : undefined
    return paymentAmount != null ? { ...base, paymentAmount } : base
  }, [machine.isEditing, editTotals, viewTotals, activeDeal])

  // Total points accounting for billing months per product (excludes removed/ended products)
  const totalPoints = useMemo(() => {
    const rows = machine.isEditing ? machine.products : productRows ?? []
    return rows.reduce((sum, p) => {
      // Skip removed or ended products
      if ((p as any)._removed || p.status === 'Ended') return sum
      const pts = Number(p.points) || 0
      if (!pts) return sum
      const dates = p.dateRange || [p.startDate, p.endDate]
      if (dates?.[0] && dates?.[1]) {
        const s = new Date(dates[0])
        const e = new Date(dates[1])
        const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1
        return sum + pts * Math.max(months, 1)
      }
      return sum + pts
    }, 0)
  }, [machine.isEditing, machine.products, productRows])
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Map validation for UI
  const { ok, errors } = useMemo(() => validate(deal, editingRows), [deal, editingRows, validate])
  const validationErrors = useMemo(() => {
    const set = new Set(errors)
    return {
      targetLaunchDate: set.has('targetLaunchDate'),
      dependenciesDueDate: set.has('dependenciesDueDate'),
      authorizedRecipient: set.has('authorizedRecipient'),
      contactRecipient: set.has('contactRecipient'),
      products: set.has('products'),
      startDate: set.has('startDate'),
      endDate: set.has('endDate'),
    }
  }, [errors])
  const { data: globalApprovers, isLoading: isApproversLoading } = useGlobalApproverSettings(true)
  const { determineApprovers } = useApproverSettings()

  const currAuthRecipient = users?.find(u => u?.user_uuid === deal?.recipients?.authorizedRecipient?.[0]?.id)
  const currContactRecipient = contacts?.find(u => u?.user_uuid === deal?.recipients?.contactRecipient?.[0]?.id)
  const authRecipient = useMemo(() => {
    const recs = document?.recipients ?? []
    return recs.find(r => r.role === 'Authorized Personnel' || r.roles?.includes('Authorized Personnel')) ?? null
  }, [document])

  const contactRecipient = useMemo(() => {
    const recs = document?.recipients ?? []
    return recs.find(r => r.role === 'Client' || r.roles?.includes('Client')) ?? null
  }, [document])
  const isCurrentAuthorizedRecipient = useMemo(() => {
    if (!authRecipient || !users || isUsersLoading) return false
    return authRecipient?.contact_id === currAuthRecipient?.panda_contact_id
  }, [authRecipient, users, isUsersLoading])
  const isCurrentContactRecipient = useMemo(() => {
    if (!contactRecipient || !contacts || isContactsLoading) return false
    return contactRecipient?.contact_id === currContactRecipient?.panda_contact_id
  }, [contactRecipient, contacts, isContactsLoading])
  const approverObjects = useMemo(() => {
    if (
      !deal?.type ||
      !Array.isArray(productRows) ||
      !productRows.length ||
      !globalApprovers ||
      isUsersLoading ||
      isApproversLoading
    ) {
      return []
    }
    return determineApprovers(deal, productRows, globalApprovers)
  }, [deal, productRows, globalApprovers, isUsersLoading, isApproversLoading, determineApprovers])

  const customByServiceKey = useMemo(() => {
    const map = new Map<string, { price: number; margin: number }>()
    for (const p of existingCustomProducts ?? []) {
      const key = String(p?.serviceKey ?? '').trim() || toServiceKey(p?.productName || p?.title || p?.name || '')
      if (!key) continue

      map.set(key, {
        price: Number(p?.price ?? 0),
        margin: Number(p?.margin ?? 0),
      })
    }
    return map
  }, [existingCustomProducts])
  ///////////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////// Screen Size //////////////////////////////////////
  const isMacbookPro = useMediaQuery(
    '(min-width: 1500px) and (max-width: 1750px) and (min-height: 850px) and (max-height: 960px)'
  )
  const baseOffset = isMacbookPro ? 135 : 160
  const navHeight = pinned ? 50 : 100
  const contentHeight = `calc(100vh - ${baseOffset + navHeight}px)`
  const calcHeight = pinned ? `calc(${contentHeight} - 40px)` : contentHeight
  ///////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////// Handle Functions //////////////////////////////////
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    try {
      await createInternalContract(
        setIsCreating,
        deal,
        currAuthRecipient,
        currContactRecipient,
        activeUser,
        //@ts-ignore
        organization,
        productRows,
        totals
      )
    } catch (e) {
      console.error('Error creating contract', e)
    }
  }

  const setContactRecipients = useCallback(
    (list: any[]) => {
      if (!activeDeal) return
      machine.updateDealField('recipients', {
        authorizedRecipient: activeDeal.recipients?.authorizedRecipient ?? [],
        contactRecipient: (list ?? []).map(toClient),
      })
    },
    [activeDeal, machine.updateDealField]
  )

  const setAuthorizedRecipients = useCallback(
    (list: any[]) => {
      if (!activeDeal) return
      machine.updateDealField('recipients', {
        authorizedRecipient: (list ?? []).map(toAuthorized),
        contactRecipient: activeDeal.recipients?.contactRecipient ?? [],
      })
    },
    [activeDeal, machine.updateDealField]
  )

  const onChange = useCallback(
    (field: keyof NewDeal, value: any) => {
      if (!activeDeal) return

      if (field === 'noSignature') {
        const checked = !!value
        machine.patchDealDraft({
          noSignature: checked,
          status: checked ? 'Completed Internally' : 'Draft',
          stage: checked ? 'Deal Won' : 'Proposal',
          ...(checked ? { timeCompleted: new Date().toISOString() } : {}),
        })
        return
      }
      if (field === 'type') {
        machine.patchDealDraft({
          type: value,
          name: `${organization?.name} - ${value}`,
        })
        return // Fix: prevent fall-through to updateDealField
      }

      // All other fields
      machine.updateDealField(field as string, value)
    },
    [activeDeal, machine.patchDealDraft, machine.updateDealField, organization?.name]
  )
  const onSaveAll = async () => {
    if (!activeDeal) return
    const { PK: dealPK, SK: dealSK } = activeDeal
    const rowsToSave = [...machine.products]
    // Capture current changes before any state transitions
    const currentUpdates = machine.dealUpdates
    const currentProductChanges = machine.productChanges
    const currentRecipientsChanged = machine.recipientsChanged
    const totalsSaved = computeTotalsFn(activeDeal, rowsToSave)

    // server updates payload - add totals to updates
    const updatesForServer = [
      ...currentUpdates.filter(u => u.fieldName !== 'totals'),
      {
        tableName: 'ORGANIZATIONS_TABLE',
        PK: dealPK,
        SK: dealSK,
        fieldName: 'totals',
        newValue: totalsSaved,
      },
    ]

    // Use optimistic save with progress tracking
    const result = await saveOptimistic({
      deal: { ...activeDeal, totals: totalsSaved },
      dealUpdates: updatesForServer,
      currentProducts: rowsToSave,
      productChanges: currentProductChanges,
      recipientsChanged: currentRecipientsChanged,
    })

    if (!result.ok) {
      console.error('Save failed:', result.error)
      return undefined
    }

    // Build the saved deal shape
    const savedDeal: NewDeal = result.deal ?? {
      ...activeDeal,
      totals: totalsSaved,
      products: rowsToSave,
    }
    const savedProducts = result.products ?? rowsToSave

    // Atomically reset machine state: baselines update + exit edit mode
    machine.saveSuccess(savedDeal, savedProducts)
    // Sync React Query cache with saved data
    restoreProductRows(savedProducts)

    return { savedDeal, rowsSaved: savedProducts, totalsSaved }
  }
  const saveAllAndUpdate = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    try {
      const result = await onSaveAll()
      if (!result) return

      const { savedDeal, rowsSaved, totalsSaved } = result

      // Build the exact payload for PandaDoc off of what we *know* we saved
      const cleanedDeal: NewDeal = {
        ...savedDeal,
        creation_timestamp: savedDeal?.creation_timestamp ?? new Date(),
        recipients: {
          authorizedRecipient: savedDeal?.recipients?.authorizedRecipient?.map(rec => ({
            ...rec,
            contact_id: currAuthRecipient?.panda_contact_id || currAuthRecipient?.contact_id,
          })),
          contactRecipient: savedDeal?.recipients?.contactRecipient?.map(rec => ({
            ...rec,
            contact_id: currContactRecipient?.panda_contact_id || currContactRecipient?.contact_id,
          })),
        },
        isCreated: true,
        products: rowsSaved,
        totals: totalsSaved,
      }

      const oldAuth = document?.recipients?.find(r => r.role === 'Authorized Personnel')
      const oldClient = document?.recipients?.find(r => r.role === 'Client')

      const newAuthId = cleanedDeal.recipients.authorizedRecipient?.[0]?.contact_id
      const newClientId = cleanedDeal.recipients.contactRecipient?.[0]?.contact_id

      const authChanged = !!oldAuth && oldAuth.contact_id !== newAuthId
      const clientChanged = !!oldClient && oldClient.contact_id !== newClientId

      const documentData = { deal: cleanedDeal, organization, activeUser, document }

      if (authChanged || clientChanged) {
        if (cleanedDeal.status === 'Completed Internally' || cleanedDeal.noSignature) {
          showSnackbar(`Signer's don't exist on internal contracts, aborting reassignment`, 'warning')
          return
        }
        await reassignRecipients(cleanedDeal, document, documentData)
      }

      updateDocument(documentData, {
        onSuccess: data => {
          if (!data.id) return

          const updates = [
            {
              tableName: 'ORGANIZATIONS_TABLE',
              PK: cleanedDeal.PK,
              SK: cleanedDeal.SK,
              fieldName: 'contractId',
              newValue: data.id,
            },
          ]

          updateFields(
            { updates },
            {
              onSuccess: () => {
                showSnackbar('Deal updated successfully', 'success')
                syncDealsCache(queryClient, cleanedDeal)
              },
              onError: error => {
                console.error('Failed to update deal fields:', error)
                showSnackbar('Failed to update deal fields', 'error')
              },
            }
          )
        },
      })
    } catch (e) {
      console.error('Error saving deal, products or updating panda doc document', e)
    }
  }
  useEffect(() => {
    if (!machine.isEditing) {
      lastStampRef.current = null
      setShowRefreshBanner(false)
      return
    }

    if (lastStampRef.current == null) {
      lastStampRef.current = String(finishedAt)
      return
    }

    if (String(finishedAt) !== lastStampRef.current) {
      setShowRefreshBanner(true)
      lastStampRef.current = String(finishedAt)
    }
  }, [machine.isEditing, finishedAt])

  const cancelEditing = () => {
    machine.cancelEdit()
  }

  const handleSortChange = useCallback(
    (newMode: ProductSortMode) => {
      setSortMode(newMode)
      if (machine.isEditing) {
        machine.sortProductsDraft(sortProducts([...machine.products], newMode))
      }
    },
    [machine.isEditing, machine.products, machine.sortProductsDraft]
  )

  // Sorted product rows for view mode
  const sortedProductRows = useMemo(() => sortProducts([...(productRows ?? [])], sortMode), [productRows, sortMode])
  const handleAddProduct = useCallback(() => {
    const d = machine.deal
    if (!d) return
    const uid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
    const maxOrder = machine.products.reduce((m, p) => Math.max(m, p.sortOrder ?? -1), -1)
    const raw: Partial<Product> = {
      PK: d.PK,
      SK: `PRODUCT_${uid}`,
      deal_uuid: d.deal_uuid,
      instanceId: uid,
      rowId: uid,
      sortOrder: maxOrder + 1,
      productName: '',
      qty: 1,
      price: 0,
      margin: 0,
      payer: 'GRO',
      accountUsed: 'GRO',
      startDate: d.startDate as any,
      endDate: d.endDate as any,
      dateRange: [d.startDate, d.endDate] as any,
      createdAt: new Date().toISOString(),
      EntityType: 'product',
      status: '',
    }
    machine.addProduct(normalizeProduct(raw as Product, 0, undefined, machine.deal?.billingModel as any))
  }, [machine.deal, machine.products, machine.addProduct])

  const handleRemoveProduct = useCallback(
    (idOrSk: string) => {
      const sk = idOrSk.startsWith('PRODUCT_') ? idOrSk : `PRODUCT_${idOrSk}`
      const index = machine.products.findIndex(p => p.SK === sk || p.instanceId === idOrSk)
      if (index >= 0) machine.removeProduct(index)
    },
    [machine.products, machine.removeProduct]
  )
  // Show blocking overlay when:
  // - Initial load (no data yet), OR
  // - Search indexes not ready AND products haven't loaded yet (prevents flashing empty on refresh)
  // - Items still loading AND deal has no products yet (covers direct-fetch fallback timing)
  const isInitialLoading =
    (isItemsLoading && !deal) ||
    (!isSearchReady && !productRows?.length && !!deal) ||
    (isItemsLoading && !!deal && !productRows?.length)
  const isSyncing = isItemsFetching && !!deal // Background sync while we have data
  return (
    <>
      {/* Save Progress Indicator - Phase 6 */}
      <SaveProgressIndicator
        progress={saveProgress}
        isEditing={machine.isEditing}
        isDirty={machine.isDirty}
        dealChangesCount={machine.dealUpdates?.length ?? 0}
        productChangesCount={
          (machine.productChanges?.created?.length ?? 0) +
          (machine.productChanges?.updated?.length ?? 0) +
          (machine.productChanges?.deletedIds?.length ?? 0)
        }
        position="top-right"
      />

      {machine.isEditing && showRefreshBanner && (
        <Box
          sx={{
            position: 'fixed',
            top: pinned ? 120 : 170, // Adjusted to account for progress indicator
            right: 16,
            zIndex: theme => theme.zIndex.snackbar - 1,
            maxWidth: 520,
            width: { xs: 100, sm: 200, md: 300, lg: 350, xl: 500 },
            pointerEvents: 'none',
          }}
        >
          <Alert
            severity="info"
            variant="outlined"
            onClose={() => setShowRefreshBanner(false)}
            sx={{
              borderRadius: 2,
              pointerEvents: 'auto',
            }}
          >
            <Stack sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ color: 'text.warning', fontSize: { xs: 10, sm: 10, md: 12, lg: 14, xl: 12 } }}>
                Background server refresh occured
              </Typography>
              <Typography sx={{ color: 'text.highlight', fontSize: { xs: 10, sm: 10, md: 12, lg: 14, xl: 12 } }}>
                your edits are safe. Save when ready.
              </Typography>
            </Stack>
          </Alert>
        </Box>
      )}
      {!isInitialLoading ? (
        <AgreementContainer container spacing={1} py={2} pt={3} px={2} sx={{ position: 'relative' }}>
          {/* Background Sync Indicator - shows when fetching with existing data */}
          {isSyncing && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.text.primary, 0.06),
                backdropFilter: 'blur(4px)',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.4, transform: 'scale(0.8)' },
                    '50%': { opacity: 1, transform: 'scale(1.2)' },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                Syncing...
              </Typography>
            </Box>
          )}

          {/* Deal Info Panel (Sidebar) — collapsible */}
          <Grid
            item
            xs={12}
            sm={sidebarCollapsed ? 0.6 : 4.5}
            md={sidebarCollapsed ? 0.5 : 4.5}
            lg={sidebarCollapsed ? 0.4 : 3.9}
            xl={sidebarCollapsed ? 0.3 : 2.9}
            sx={{ transition: 'all 0.3s ease' }}
          >
            <DealInfoPanel
              sx={{
                height: calcHeight,
                bgcolor: 'background.default',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Collapse/Expand toggle */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-end',
                  p: 0.5,
                }}
              >
                <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                  <IconButton
                    size="small"
                    onClick={() => setSidebarCollapsed(prev => !prev)}
                    sx={{
                      width: 24,
                      height: 24,
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              {sidebarCollapsed ? (
                /* Collapsed: vertical strip with key totals */
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    pt: 1,
                  }}
                >
                  <Tooltip title={`Monthly: $${(displayTotals?.monthlyTotal ?? 0).toLocaleString()}`} placement="right">
                    <Typography
                      sx={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'text.secondary',
                        letterSpacing: '0.05em',
                      }}
                    >
                      ${(displayTotals?.monthlyTotal ?? 0).toLocaleString()}/mo
                    </Typography>
                  </Tooltip>
                  <Tooltip title={`Total: $${(displayTotals?.total ?? 0).toLocaleString()}`} placement="right">
                    <Typography
                      sx={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        fontSize: 10,
                        fontWeight: 500,
                        color: 'text.disabled',
                      }}
                    >
                      ${(displayTotals?.total ?? 0).toLocaleString()}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : machine.isEditing ? (
                <AgreementTotalsEdit
                  deal={activeDeal}
                  organization={organization}
                  productRows={editingRows}
                  totals={totals}
                  validationErrors={validationErrors}
                  onChange={onChange}
                  onChangeContactRecipients={setContactRecipients}
                  onChangeAuthorizedRecipients={setAuthorizedRecipients}
                />
              ) : (
                <AgreementTotalsView
                  activeUser={activeUser}
                  deal={activeDeal}
                  organization={organization}
                  totals={displayTotals}
                  validationErrors={validationErrors}
                  document={document}
                  isCurrentContactRecipient={isCurrentContactRecipient}
                  isCurrentAuthorizedRecipient={isCurrentAuthorizedRecipient}
                  approverObjects={approverObjects}
                  productRows={editingRows}
                  isDraft={activeDeal?.isDraft ?? false}
                  showPointsView={showPointsView}
                  onTogglePointsView={setShowPointsView}
                />
              )}
            </DealInfoPanel>
          </Grid>

          {/* Products Table Section */}
          <Grid
            item
            xs={12}
            sm={sidebarCollapsed ? 11.4 : 6.5}
            md={sidebarCollapsed ? 11.5 : 7.2}
            lg={sidebarCollapsed ? 11.6 : 8.1}
            xl={sidebarCollapsed ? 11.7 : 9.1}
            pr={1}
            bgcolor="background.section"
            sx={{ transition: 'all 0.3s ease' }}
          >
            {/* Contract Flow Timeline */}
            {!machine.isEditing && activeDeal && (
              <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                <ContractFlowTimeline deal={activeDeal} document={document} />
              </Box>
            )}
            <ContractToolbar
              sortMode={sortMode}
              onSortChange={handleSortChange}
              isEditing={machine.isEditing}
              isDirty={machine.isDealDirty}
              productsDirty={machine.isProductsDirty}
              isSaveRequired={isSaveRequired}
              ok={ok}
              deal={activeDeal}
              productRows={editingRows}
              productChanges={machine.productChanges}
              activeUser={activeUser}
              onSaveAll={onSaveAll}
              onSaveAndUpdate={saveAllAndUpdate}
              onCancel={cancelEditing}
              onEdit={onEditProducts}
              onRequest={async () => {
                await handleApprovalRequest({
                  deal: activeDeal,
                  ok,
                  productCount: editingRows?.length ?? 0,
                  isDirty: machine.isDealDirty,
                  productsDirty: machine.isProductsDirty,
                  approverObjects,
                  activeUser,
                  onSaveAll,
                  showSnackbar,
                })
                // No requestAnimationFrame baseline reset needed:
                // onSaveAll() calls machine.saveSuccess() which atomically resets baselines
              }}
              onCreate={handleSubmit}
              isRequesting={isRequesting}
              isCreating={isCreating}
              validationErrors={validationErrors}
              resetDealBaseline={machine.resetBaselines}
              compact={isAgentDocked}
            />
            <Box>
              {machine.isEditing ? (
                <ProductsSection>
                  <ProductRowsTable
                    deal={activeDeal}
                    productRows={editingRows}
                    onDragEnd={result => {
                      const { source, destination } = result
                      if (!destination || source.index === destination.index) return
                      machine.reorderProducts(source.index, destination.index)
                    }}
                    onProductChange={(index, field, value, additional) => {
                      // Block edits to derived fields
                      if (field === 'mediaSpend' || field === 'grossProfit' || field === 'billedPrice') return

                      const normalized = MONEY_FIELDS.has(field as string) ? toMoney(value) : value
                      const currentRow = machine.products[index]
                      if (!currentRow) return

                      const extras = additional ?? {}
                      const clientMargin = field === 'payer' && value === 'Client' ? 15 : 0

                      const updated = calcUpdateProductField(
                        currentRow,
                        field as keyof Product,
                        normalized,
                        extras,
                        clientMargin
                      )
                      machine.updateProduct(index, updated)
                    }}
                    validationErrors={validationErrors}
                    removeRow={handleRemoveProduct}
                    containerHeight={`calc(${calcHeight} - 60px)`}
                    listRef={scrollRef}
                    customByServiceKey={customByServiceKey}
                  />
                </ProductsSection>
              ) : (
                <BoxScroller
                  sx={{
                    maxHeight: `calc(${calcHeight} - 10px)`,
                    overflowY: 'auto',
                    bgcolor: 'background.section',
                    backgroundSize: '10px 10px',
                  }}
                >
                  <ProductRowsTableView
                    deal={activeDeal}
                    productRows={sortedProductRows}
                    showPointsView={showPointsView}
                  />
                </BoxScroller>
              )}
            </Box>

            {/* Totals Bar */}
            {activeDeal && (
              <ContractTotalsBar
                deal={activeDeal}
                monthlyTotal={displayTotals?.monthlyTotal ?? 0}
                implementationFee={displayTotals?.implementationFee ?? 0}
                grossProfit={displayTotals?.grossProfit ?? 0}
                totalPoints={activeDeal?.billingModel === 'points' ? totalPoints : undefined}
                setupFee={displayTotals?.setupFee ?? 0}
                total={displayTotals?.total ?? 0}
                isCSA={['CSA', 'RCSA'].includes(String(activeDeal?.type ?? '').toUpperCase())}
                paymentAmount={(displayTotals as any)?.paymentAmount}
                isEditing={machine.isEditing}
                onAddProduct={handleAddProduct}
              />
            )}
          </Grid>
        </AgreementContainer>
      ) : (
        <GROBxLoader open={isInitialLoading} message={'Loading Agreement'} overlay />
      )}
    </>
  )
}
export default ViewAgreement
